import useFullyFledgedField from '@gef-ui/form-core/hooks/field/useFullyFledgedField';
import { defaultObjectCreateOptions } from '@gef-ui/form-core/utils/autocomplete';
import { getMessage } from '@gef-ui/localization/messages';
import chainHandlers from "@gef-ui/utils/chainHandlers";
import MuiAutocomplete from '@mui/material/Autocomplete';
import PropTypes from 'prop-types';
import T from "ramda/es/T";
import always from "ramda/es/always";
import equals from "ramda/es/equals";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { validationDisplayTypes } from '../../constants';
import { resolveValidationColor, useFieldDefaultValue } from '../../utils';
import { DEFAULT_VALUE_PROP_NAME } from '../constants';
import { useAutocompleteWithValueData } from '../hooks';
import { renderAutocompleteInput, renderAutocompleteTags, resolveAutocompleteVariant } from '../utils';
export const COMPONENT_NAME = 'Autocomplete';
const EMPTY_ARRAY = [];
const Autocomplete = (inProps)=>{
    const props = useFieldDefaultValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { fieldName, fieldIndices, initiallyDirty, viewParser, modelParser, onBlur, onChange, onFocus, handleInputChange, multiple, freeSolo, createOptions = defaultObjectCreateOptions, optionsFilter = T, createOptionsFilter = always(optionsFilter), options, disabled: disabledByProp, defaultValue, validationDisplay = validationDisplayTypes.PLAIN_TEXT, label, valuePropName = DEFAULT_VALUE_PROP_NAME, labelPropName = 'name', extraInputProps, TextFieldProps, helperText, noOptionsText = getMessage('common:noRecord'), isLoading, inputReadOnly, tooltip, variant, ...other } = props;
    const { value, errorMessage, handleBlur, handleChange, setFocusFalse, setFocusTrue, localizedLabel, fieldId, disabled, asyncValidationInProgress, validationType, errMeta = [], hidden } = useFullyFledgedField({
        fieldName,
        fieldIndices,
        initiallyDirty,
        modelParser,
        viewParser,
        disabled: disabledByProp,
        defaultValue
    });
    const validationColor = useMemo(()=>resolveValidationColor(validationType, validationDisplay), [
        validationType,
        validationDisplay
    ]);
    const { handleValueChange, computedOptions, valueLabel } = useAutocompleteWithValueData({
        handleChange,
        onChange,
        freeSolo,
        createOptions,
        optionsFilter: createOptionsFilter(props),
        options,
        value,
        valuePropName,
        labelPropName
    });
    const [inputValue, setInputValue] = useState();
    useEffect(()=>{
        if (freeSolo && inputValue !== value) {
            setInputValue(value);
        }
    }, [
        freeSolo,
        inputValue,
        value
    ]);
    const inputChangeHandler = useCallback((event, value, reason)=>{
        setInputValue(value);
        if (freeSolo && !multiple) {
            handleValueChange(event, value, reason);
        }
        handleInputChange?.(event, value, reason);
    }, [
        freeSolo,
        multiple,
        handleInputChange,
        handleValueChange
    ]);
    return /*#__PURE__*/ React.createElement(MuiAutocomplete, {
        id: fieldId,
        disabled: disabled,
        multiple: multiple,
        freeSolo: freeSolo,
        value: multiple && !valueLabel ? EMPTY_ARRAY : valueLabel,
        onChange: handleValueChange,
        inputValue: inputValue || '',
        onInputChange: inputChangeHandler,
        onBlur: chainHandlers([
            setFocusFalse,
            handleBlur,
            onBlur
        ]),
        onFocus: chainHandlers([
            setFocusTrue,
            onFocus
        ]),
        options: computedOptions,
        isOptionEqualToValue: equals,
        renderTags: renderAutocompleteTags({
            errMeta,
            freeSolo,
            validationColor
        }),
        renderInput: renderAutocompleteInput({
            errorMessage,
            localizedLabel,
            asyncValidationInProgress,
            validationColor,
            validationDisplay,
            label,
            extraInputProps,
            helperText,
            isLoading,
            inputReadOnly,
            tooltip,
            variant,
            hidden,
            ...TextFieldProps
        }),
        noOptionsText: noOptionsText,
        ...resolveAutocompleteVariant(variant),
        ...other
    });
};
Autocomplete.propTypes = {
    TextFieldProps: PropTypes.object,
    createOptions: PropTypes.func,
    createOptionsFilter: PropTypes.func,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    extraInputProps: PropTypes.object,
    fieldIndices: PropTypes.arrayOf(PropTypes.number),
    fieldName: PropTypes.string,
    freeSolo: PropTypes.bool,
    handleInputChange: PropTypes.func,
    helperText: PropTypes.node,
    initiallyDirty: PropTypes.bool,
    inputReadOnly: PropTypes.bool,
    isLoading: PropTypes.bool,
    label: PropTypes.string,
    labelPropName: PropTypes.string,
    modelParser: PropTypes.func,
    multiple: PropTypes.bool,
    noOptionsText: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    optionsFilter: PropTypes.func,
    tooltip: PropTypes.string,
    validationDisplay: PropTypes.oneOf(Object.keys(validationDisplayTypes)),
    valuePropName: PropTypes.string,
    variant: PropTypes.string,
    viewParser: PropTypes.func
};
export default Autocomplete;
